/**
 * settings/cards/MyDetailsCard.tsx
 * ---------------------------------
 * What: Shows and lets the user edit basic account information.
 * Why: Common settings task presented in a compact card.
 */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  useWindowDimensions,
  Platform,
  Animated,
  Alert,
  Modal,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { supabase } from '../../../../lib/supabase';
import { useAuth } from '../../../../lib/AuthContext';
import { useTheme } from '../../../../lib/ThemeContext';
import { useSubscription } from '../../../../lib/SubscriptionContext';
import FormTextInput from '../../../components/FormTextInput';
import ThemedButton from '../../../components/ThemedButton';

interface Props {
  expanded: boolean;
  onToggle: () => void;
}

export default function MyDetailsCard({ expanded, onToggle }: Props) {
  const { width } = useWindowDimensions();
  const isSmallScreen = width < 768;

  const { theme, themeType } = useTheme();
  const { user } = useAuth();
  useSubscription(); // keep hook for future if needed

  // ——— State (email change) ———
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [currentPasswordForEmail, setCurrentPasswordForEmail] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  // ——— State (password change) ———
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showCurrentPasswordForEmail, setShowCurrentPasswordForEmail] = useState(false);

  // Modals
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  // Glow style
  const glowStyle = useMemo(() => (
    themeType === 'blueNeon'
      ? (Platform.OS === 'web'
          ? { boxShadow: `0 0 6px 1px ${theme.accent}` } as any
          : {
              shadowColor: theme.accent,
              shadowOffset: { width: 0, height: 0 },
              shadowOpacity: 0.5,
              shadowRadius: 6,
              elevation: 6,
            })
      : {}
  ), [themeType, theme.accent]);

  /* ────────────────────────────────────────────────────────────
     Handlers
  ──────────────────────────────────────────────────────────── */
  const handleChangeEmail = async () => {
    if (!user || !user.email) {
      setEmailMessage('User not loaded.');
      return;
    }
    if (!newEmail || !currentPasswordForEmail) {
      setEmailMessage('Please fill in all fields.');
      return;
    }
    setEmailLoading(true);
    // Re-authenticate user
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: currentPasswordForEmail,
    });
    if (signInError) {
      setEmailMessage('Current password is incorrect.');
      setEmailLoading(false);
      return;
    }
    // Update email
    const { error } = await supabase.auth.updateUser(
      { email: newEmail },
      {
        // On web, send users back to the app with a recognizable marker
        emailRedirectTo:
          typeof window !== 'undefined'
            ? `${window.location.origin}/?auth=email_change`
            : undefined,
      }
    );
    if (error) {
      setEmailMessage(error.message);
    } else {
      setEmailMessage('');
      setNewEmail('');
      setCurrentPasswordForEmail('');
      setShowEmailModal(true);
    }
    setEmailLoading(false);
  };

  const handleChangePassword = async () => {
    if (!user || !user.email) {
      setErrorModalMessage('User not loaded.');
      setShowErrorModal(true);
      return;
    }
    if (!currentPassword || !newPassword || !confirmPassword) {
      setErrorModalMessage('Please fill in all fields.');
      setShowErrorModal(true);
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorModalMessage('New passwords do not match.');
      setShowErrorModal(true);
      return;
    }
    setPasswordLoading(true);
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: currentPassword,
    });
    if (signInError) {
      setErrorModalMessage('Current password is incorrect.');
      setShowErrorModal(true);
      setPasswordLoading(false);
      return;
    }
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      setErrorModalMessage(error.message);
      setShowErrorModal(true);
    } else {
      setShowSuccessModal(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordForm(false);
    }
    setPasswordLoading(false);
  };

  /* ──────────────────────────────────────────────────────────── */
  const cardBase = [styles.cardBase, { backgroundColor: theme.card, borderColor: theme.border }, glowStyle];

  const TextInput = FormTextInput as any;

  return (
    <View style={cardBase}> 
      {/* Accordion Header */}
      <TouchableOpacity style={[styles.accordionHeader, expanded && { marginBottom: 16 }]} onPress={onToggle}>
        <Text style={[styles.detailsTitle, { color: theme.text, marginBottom: 0 }]}>My Details</Text>
        <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={24} color={theme.text} />
      </TouchableOpacity>

      {expanded && (
        <>
          <View style={{ height: 1, backgroundColor: theme.border, width: '100%', marginBottom: 16 }} />
          <View style={[styles.detailsRow, isSmallScreen && styles.detailsRowColumn]}>
            <Text style={[styles.detailsLabel, { color: theme.text }]}>Email:</Text>
            <Text style={[styles.detailsValue, { color: theme.text }]}>{user?.email ?? ''}</Text>
          </View>

          {/* Change Email Button */}
          <TouchableOpacity
            style={[styles.actionButton, { marginTop: 12, backgroundColor: theme.accent }]}
            onPress={() => {
              setShowEmailForm((prev) => !prev);
              setShowPasswordForm(false);
            }}
          >
            <Ionicons name="mail-outline" size={20} color={theme.buttonText} />
            <Text style={[styles.actionButtonText, { color: theme.buttonText }]}>Change Email</Text>
          </TouchableOpacity>

          {showEmailForm && (
            <View style={styles.formSection}>
              <Text style={[styles.sectionTitle, { color: theme.text }]}>Change Email</Text>
              <Text style={[styles.label, { color: theme.text }]}>New Email</Text>
              <View style={styles.inputGroup}>
                <TextInput
                  style={[styles.input, { backgroundColor: theme.inputBackground, borderColor: theme.border, color: theme.text }]}
                  placeholder="Enter new email"
                  placeholderTextColor={theme.secondaryText}
                  value={newEmail}
                  onChangeText={setNewEmail}
                  autoCapitalize="none"
                  keyboardType="email-address"
                  editable={!!user}
                />
              </View>
              <Text style={[styles.label, { color: theme.text }]}>Current Password</Text>
              <View style={styles.passwordContainer}>
                <TextInput
                  style={[styles.input, styles.passwordInput, { backgroundColor: theme.inputBackground, borderColor: theme.border, color: theme.text }]}
                  placeholder="Enter current password"
                  placeholderTextColor={theme.secondaryText}
                  value={currentPasswordForEmail}
                  onChangeText={setCurrentPasswordForEmail}
                  secureTextEntry={!showCurrentPasswordForEmail}
                  editable={!!user}
                />
                <TouchableOpacity style={styles.eyeButton} onPress={() => setShowCurrentPasswordForEmail(!showCurrentPasswordForEmail)}>
                  <Ionicons name={showCurrentPasswordForEmail ? 'eye-off' : 'eye'} size={24} color={theme.secondaryText} />
                </TouchableOpacity>
              </View>
              {emailMessage ? <Text style={[styles.message, { color: theme.text }]}>{emailMessage}</Text> : null}
              <TouchableOpacity
                style={[styles.actionButton, { marginTop: 12, backgroundColor: theme.accent }]}
                onPress={handleChangeEmail}
                disabled={emailLoading || !user}
              >
                <Ionicons name="mail-outline" size={20} color={theme.buttonText} />
                <Text style={[styles.actionButtonText, { color: theme.buttonText }]}>{emailLoading ? 'Saving…' : 'Save Email'}</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Change Password Button */}
          <TouchableOpacity
            style={[styles.actionButton, { marginTop: 12, backgroundColor: theme.accent }]}
            onPress={() => {
              setShowPasswordForm((prev) => !prev);
              setShowEmailForm(false);
            }}
          >
            <Ionicons name="lock-closed-outline" size={20} color={theme.buttonText} />
            <Text style={[styles.actionButtonText, { color: theme.buttonText }]}>Change Password</Text>
          </TouchableOpacity>

          {showPasswordForm && (
            <View style={styles.formSection}>
              <Text style={[styles.sectionTitle, { color: theme.text }]}>Change Password</Text>
              <Text style={[styles.label, { color: theme.text }]}>Current Password</Text>
              <View style={styles.passwordContainer}>
                <TextInput
                  style={[styles.input, styles.passwordInput, { backgroundColor: theme.inputBackground, borderColor: theme.border, color: theme.text }]}
                  placeholder="Enter current password"
                  placeholderTextColor={theme.secondaryText}
                  value={currentPassword}
                  onChangeText={setCurrentPassword}
                  secureTextEntry={!showCurrentPassword}
                  editable={!!user}
                />
                <TouchableOpacity style={styles.eyeButton} onPress={() => setShowCurrentPassword(!showCurrentPassword)}>
                  <Ionicons name={showCurrentPassword ? 'eye-off' : 'eye'} size={24} color={theme.secondaryText} />
                </TouchableOpacity>
              </View>

              <Text style={[styles.label, { color: theme.text }]}>New Password</Text>
              <View style={styles.passwordContainer}>
                <TextInput
                  style={[styles.input, styles.passwordInput, { backgroundColor: theme.inputBackground, borderColor: theme.border, color: theme.text }]}
                  placeholder="Enter new password"
                  placeholderTextColor={theme.secondaryText}
                  value={newPassword}
                  onChangeText={setNewPassword}
                  secureTextEntry={!showNewPassword}
                  editable={!!user}
                />
                <TouchableOpacity style={styles.eyeButton} onPress={() => setShowNewPassword(!showNewPassword)}>
                  <Ionicons name={showNewPassword ? 'eye-off' : 'eye'} size={24} color={theme.secondaryText} />
                </TouchableOpacity>
              </View>

              <Text style={[styles.label, { color: theme.text }]}>Confirm New Password</Text>
              <View style={styles.passwordContainer}>
                <TextInput
                  style={[styles.input, styles.passwordInput, { backgroundColor: theme.inputBackground, borderColor: theme.border, color: theme.text }]}
                  placeholder="Confirm new password"
                  placeholderTextColor={theme.secondaryText}
                  value={confirmPassword}
                  onChangeText={setConfirmPassword}
                  secureTextEntry={!showConfirmPassword}
                  editable={!!user}
                />
                <TouchableOpacity style={styles.eyeButton} onPress={() => setShowConfirmPassword(!showConfirmPassword)}>
                  <Ionicons name={showConfirmPassword ? 'eye-off' : 'eye'} size={24} color={theme.secondaryText} />
                </TouchableOpacity>
              </View>

              <TouchableOpacity
                style={[styles.actionButton, { marginTop: 12, backgroundColor: theme.accent }]}
                onPress={handleChangePassword}
                disabled={passwordLoading || !user}
              >
                <Ionicons name="lock-closed-outline" size={20} color={theme.buttonText} />
                <Text style={[styles.actionButtonText, { color: theme.buttonText }]}>{passwordLoading ? 'Saving…' : 'Save Password'}</Text>
              </TouchableOpacity>
            </View>
          )}
        </>
      )}

      {/* Email confirmation modal */}
      <Modal visible={showEmailModal} transparent animationType="fade" onRequestClose={() => setShowEmailModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.card }]}>
            <Text style={styles.modalTitle}>Email Change Requested</Text>
            <Text style={styles.modalMessage}>
              A confirmation link has been sent to your new email address. Please check your inbox and click the link to complete the change.
            </Text>
            <ThemedButton title="OK" onPress={() => setShowEmailModal(false)} style={{ marginTop: 8 }} />
          </View>
        </View>
      </Modal>

      {/* Error modal */}
      <Modal visible={showErrorModal} transparent animationType="fade" onRequestClose={() => setShowErrorModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.card }]}>
            <Text style={styles.modalTitle}>Error</Text>
            <Text style={styles.modalMessage}>{errorModalMessage}</Text>
            <ThemedButton title="OK" onPress={() => setShowErrorModal(false)} style={{ marginTop: 8 }} />
          </View>
        </View>
      </Modal>

      {/* Success modal */}
      <Modal visible={showSuccessModal} transparent animationType="fade" onRequestClose={() => setShowSuccessModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.card }]}>
            <Text style={styles.modalTitle}>Success</Text>
            <Text style={styles.modalMessage}>Your password has been successfully updated.</Text>
            <ThemedButton title="OK" onPress={() => setShowSuccessModal(false)} style={{ marginTop: 8, minWidth: 140, alignSelf: 'center' }} />
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  cardBase: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
  },
  accordionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  detailsTitle: {
    fontSize: 17,
    fontWeight: '500',
    fontFamily: 'Inter_500Medium',
    marginBottom: 16,
  },
  detailsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  detailsRowColumn: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  detailsLabel: {
    fontSize: 16,
    fontWeight: '600',
  },
  detailsValue: {
    fontSize: 16,
  },
  actionButton: {
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    width: 200,
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    alignSelf: 'flex-start',
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  formSection: {
    padding: 16,
    gap: 16,
    marginTop: 8,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
  },
  inputGroup: {
    marginBottom: 16,
  },
  input: {
    borderWidth: 1,
    padding: 15,
    borderRadius: 6,
    fontSize: 16,
    width: '100%',
  },
  passwordContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
    position: 'relative',
  },
  passwordInput: {
    flex: 1,
    paddingRight: 50,
  },
  eyeButton: {
    position: 'absolute',
    right: 15,
    padding: 5,
  },
  message: {
    fontSize: 16,
    marginTop: 8,
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: '500',
    fontFamily: 'Inter_500Medium',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    padding: 24,
    borderRadius: 12,
    width: '80%',
    maxWidth: 400,
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 17,
    fontWeight: '500',
    fontFamily: 'Inter_500Medium',
    marginBottom: 12,
    textAlign: 'center',
  },
  modalMessage: {
    fontSize: 16,
    marginBottom: 20,
    textAlign: 'center',
  },
}); 